import { useEffect } from 'react';

import { DrawerView } from '../../../../components/Maps/types/types';
import { trackEvent } from '@domain/analytics/trackEvent';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';
import { toNumber } from '@lib/strings/toNumber';
import { EventMykissExploreDrawerViewed } from '@generated/events';
import { useExplore } from '../../context/ExploreContext';

import { MapDrawerNotification } from './MapDrawerNotification/MapDrawerNotification';
import { CatchesView } from './Views/CatchesView/CatchesView';
import { CatchView } from './Views/CatchView/CatchView';
import { FilterCatchesView } from './Views/FilterCatchesView/FilterCatchesView';
import { FishingWaterSettingsView } from './Views/FishingWaterSettingsView/FishingWaterSettingsView';
import { FishingWaterView } from './Views/FishingWaterView/FishingWaterView';
import { MapLayersView } from './Views/MapLayersView/MapLayersView';
import { POISettingsView } from './Views/POISettingsView/POISettingsView';
import { POIView } from './Views/POIView/POIView';
import { RegulationsView } from './Views/RegulationsView/RegulationsView';
import { RegulationView } from './Views/RegulationView/RegulationView';
import { WaypointsSettingsView } from './Views/WaypointsSettingsView/WaypointsSettingsView';
import { WaypointView } from './Views/WaypointView/WaypointView';

// TODO: combine these two switches

function getEventType({
  drawerView,
  drawerViewId,
}: {
  drawerView?: DrawerView;
  drawerViewId?: string;
}): [EventMykissExploreDrawerViewed['properties']['type'], string | undefined] | null {
  if (drawerView && drawerViewId) {
    switch (drawerView) {
      case 'catch':
        return ['catch', drawerViewId];
      case 'catches':
        return ['catches', undefined];
      case 'filter-catches':
        return ['catches_filters', undefined];
      case 'fishing-water':
        return ['water', drawerViewId];
      case 'fishing-waters-settings':
        return ['fishing-waters-settings', drawerViewId];
      case 'map-layers':
        return ['mapLayers', undefined];
      case 'poi':
        return ['poi', drawerViewId];
      case 'poi-settings':
        return ['poi-settings', drawerViewId];
      case 'regulation':
        return ['regulation', drawerViewId];
      case 'regulations':
        return ['regulations', undefined];
      case 'waypoint':
        return ['waypoint', drawerViewId];
      case 'waypoints-settings':
        return ['waypoint-settings', drawerViewId];
      default:
        return null;
    }
  }
  return null;
}

export const MapDrawer = () => {
  const clientHasRendered = useClientHasRendered();
  const { exploreQueryParams } = useExplore();
  const { drawerView, drawerViewId } = exploreQueryParams;
  const drawerViewIdAsNumber = toNumber(drawerViewId);

  useEffect(() => {
    const eventType = getEventType({
      drawerView,
      drawerViewId,
    });
    if (eventType && clientHasRendered) {
      const [type, id] = eventType;
      trackEvent({
        name: 'mykiss_explore_drawer_viewed',
        properties: { type, id },
      });
    }
  }, [drawerView, drawerViewId, clientHasRendered]);

  const errorView = (
    <MapDrawerNotification
      title="Oh no!"
      description="It looks like something went wrong, try opening a different menu option"
      withIcon
    />
  );

  switch (drawerView) {
    case 'catch':
      if (!drawerViewId) {
        return errorView;
      }
      return <CatchView catchId={drawerViewId} />;
    case 'catches':
      return <CatchesView />;
    case 'filter-catches':
      return <FilterCatchesView />;
    case 'fishing-water':
      return drawerViewId ? <FishingWaterView waterId={drawerViewId} /> : errorView;
    case 'fishing-waters-settings':
      return <FishingWaterSettingsView />;
    case 'map-layers':
      return <MapLayersView />;
    case 'poi':
      return drawerViewIdAsNumber ? (
        <POIView pointOfInterestId={drawerViewIdAsNumber} />
      ) : (
        errorView
      );
    case 'poi-settings':
      return <POISettingsView />;
    case 'regulation':
      return drawerViewIdAsNumber ? (
        <RegulationView fishrulesSpeciesId={drawerViewIdAsNumber} />
      ) : (
        errorView
      );
    case 'regulations':
      return <RegulationsView />;
    case 'waypoint':
      return drawerViewId ? <WaypointView waypointId={drawerViewId} /> : errorView;
    case 'waypoints-settings':
      return <WaypointsSettingsView />;
    default:
      return errorView;
  }
};
